import { isNonemptyArray } from '../utils'
import { getFrameworkVar } from '../states'

const setPrototypeOf = Object.setPrototypeOf || ((obj, proto) => obj.__proto__ = proto)

export interface Tsg {
  display_name: string
  id: string
  parent_id?: string
  children?: Array<Tsg>
  isRoot?: boolean
  vertical?: string
  [key: string]: any
}

interface SuperRootTsg extends Tsg {
  isSuperRoot?: boolean
}

export type ReadonlyTsgTree = Readonly<TsgTree>

export const ROOT_NODE_ID = '_superRoot'
export class TsgTree extends Array<Tsg> {
  public readonly superRoot: SuperRootTsg
  public readonly children?: Array<Tsg>
  public readonly hasMsp: boolean
  private readonly _map: Map<string, Tsg>
  public readonly error?: Error
  private constructor(tree: Array<Tsg> | TsgTree, error?: Error) {
    super()
    setPrototypeOf(this, TsgTree.prototype)
    if (error) {
      this.error = error
    }
    if (Array.isArray(tree) && tree.length === 1 && tree[0].id === ROOT_NODE_ID) {
      this.children = Array.from(tree[0].children || [])
      this.superRoot = { ...tree[0], children: this.children } as SuperRootTsg
    }
    else if (tree instanceof TsgTree) {
      this.children = Array.from(tree.children as Tsg[])
      this.superRoot = { ...tree.superRoot, children: this.children } as SuperRootTsg
    }
    else {
      this.children = Array.from(tree as Tsg[])
      this.superRoot = { display_name: 'All Tenants', id: ROOT_NODE_ID, isSuperRoot: true, children: this.children } as SuperRootTsg
    }
    this.push(this.superRoot)
    this.hasMsp = this.children.length > 2 || isNonemptyArray(this.children[0]?.children)
    const map = new Map()
    if (this.children.length) {
      // TSGs for which the MSP portal link needs to be pointed to the SASE portal
      const saseTsgsRawValue = getFrameworkVar('sase_tsgs')
      const saseRootTsgs = isNonemptyArray(saseTsgsRawValue) ? new Set(saseTsgsRawValue) : undefined
      const traverse = (tsgs: Array<Tsg>) => {
        for (const tsg of tsgs) {
          // Set isSASE flag to the current tsg and its children if the tsg id is found in ths sase_tsg list
          if (saseRootTsgs?.has(tsg.id)) {
            tsg.isSASE = true
          }
          map.set(tsg.id, Object.defineProperties(tsg, {
            tsg_id: { value: tsg.id },
          }))
          if (isNonemptyArray(tsg.children)) {
            if (tsg.isSASE) {
              for (const child of tsg.children) {
                child.isSASE = true
              }
            }
            traverse(tsg.children)
          }
        }
      }
      for (const root of this.children) {
        if (!root.parent_id) {
          root.isAbsoluteRoot = true
        }
        root.parent_id = ROOT_NODE_ID // overwrite parent_id
        root.isRoot = true
      }
      traverse([this.superRoot])
    }
    this._map = map
  }

  public get list(): Array<Tsg> {
    return Array.from(this._map.values())
  }

  public get count(): number {
    return this._map.size - 1 // exclude _superRoot
  }

  public isEmpty(): boolean {
    return !this.children?.length
  }

  public hasSubTenants(tsg_id: string | undefined): boolean {
    const tsg = this.get(tsg_id)
    return Boolean(tsg && isNonemptyArray(tsg.children))
  }

  public hasParentTenant(tsg_id: string | undefined): boolean {
    const tsg = this.get(tsg_id)
    return Boolean(tsg?.parent_id && tsg.parent_id !== ROOT_NODE_ID)
  }

  public hasParentOrSubTenants(tsg_id: string | undefined): boolean {
    const tsg = this.get(tsg_id)
    return Boolean(tsg && (isNonemptyArray(tsg.children) ||
      (tsg.parent_id && tsg.parent_id !== ROOT_NODE_ID)))
  }

  public has(tsg_id: string | undefined): boolean {
    return tsg_id ? this._map.has(tsg_id) : false
  }

  public get(tsg_id: string | undefined): Tsg | undefined {
    return tsg_id ? this._map.get(tsg_id) : undefined
  }

  public map(callbackfn: (value: Tsg, index: number, array: Tsg[]) => any, thisArg: any = this): Array<any> {
    return [callbackfn.call(thisArg, this.superRoot, 0, this)]
  }

  public filter(callbackfn: (value: Tsg, index: number, array: Tsg[]) => any, thisArg: any = this): Array<any> {
    return callbackfn.call(thisArg, this.superRoot, 0, this) ? [this.superRoot] : []
  }

  static from(tree: Array<Tsg> | TsgTree): ReadonlyTsgTree {
    if (Object.isFrozen(tree) && (tree instanceof TsgTree)) {
      return tree
    }
    return Object.freeze(new TsgTree(tree))
  }

  static fromError(error: Error | TsgTree): ReadonlyTsgTree {
    if (Object.isFrozen(error) && (error instanceof TsgTree)) {
      return error
    }
    return Object.freeze(new TsgTree([], error as Error)) as TsgTree
  }
}

export type IndexTsgTree = ((tree: Array<Tsg> | TsgTree) => ReadonlyTsgTree) & {
  fromError: (error: Error | TsgTree) => ReadonlyTsgTree
}
export const indexTsgTree: IndexTsgTree = (tree) => TsgTree.from(tree)
indexTsgTree.fromError = TsgTree.fromError

export default indexTsgTree
